<template>
  <app-content class="h-panel">
    <Loading text="数据加载中..." :loading="loading"></Loading>
    <div class="h-panel-bar">
      <span class="h-panel-title">现金流量表</span>
      <div class="h-panel-right">
        <Button color="primary" @click="doSearch" :loading="loading">查询</Button>
        <Button @click="doExport">导出Excel</Button>
      </div>
    </div>
    <Row type="flex" :space-x="10" class="margin-right-left margin-top">
      <Cell>
        <account-date-choose v-model="accountDate"/>
      </Cell>
    </Row>

    <div class="h-panel-body" v-if="report">
      <!-- 勾稽状态提示 -->
      <div v-if="reconciliation" class="reconciliation-status" :class="{passed: reconciliation.passed, failed: !reconciliation.passed}">
        <Row type="flex" :space-x="20">
          <Cell>
            <strong>勾稽状态：</strong>
            <span v-if="reconciliation.passed" class="text-success">✓ 通过</span>
            <span v-else class="text-danger">✗ 不通过</span>
          </Cell>
          <Cell>
            <strong>勾稽差额：</strong>
            <span>{{ formatMoney(reconciliation.reconciliationDifference) }}</span>
          </Cell>
          <Cell v-if="reconciliation.pendingCount > 0">
            <strong class="text-warning">待确认：</strong>
            <span class="text-warning">{{ reconciliation.pendingCount }} 笔，金额 {{ formatMoney(reconciliation.pendingAmount) }}</span>
            <Button size="small" @click="goToPending" class="margin-left">查看待确认</Button>
          </Cell>
        </Row>
      </div>

      <Table class="small-td" :datas="templateItems" selectRow border>
        <TableItem title="名称">
          <div slot-scope="{data}" :class="{'font-bold':data.isBolder}" :style="{paddingLeft:((data.level-1)*15)+'px'}">{{data.title}}</div>
        </TableItem>
        <TableItem title="行次">
          <template slot-scope="{data}">
            {{data.isClassified ? '':data.lineNum>-1?data.lineNum:''}}
          </template>
        </TableItem>
        <TableItem title="本年累计金额" align="right">
          <template slot-scope="{data}">
            {{reportData[data.id] ? formatMoney(reportData[data.id].currentYearAmount) : ''}}
          </template>
        </TableItem>
        <TableItem title="本期金额" align="right">
          <template slot-scope="{data}">
            {{reportData[data.id] ? formatMoney(reportData[data.id].currentPeriodAmount) : ''}}
          </template>
        </TableItem>
      </Table>

      <!-- 勾稽详情 -->
      <div v-if="reconciliation" class="reconciliation-detail">
        <h4>勾稽详情</h4>
        <Row type="flex" :space-x="20">
          <Cell>
            <strong>经营活动净额：</strong> {{ formatMoney(reconciliation.operatingNet) }}
          </Cell>
          <Cell>
            <strong>投资活动净额：</strong> {{ formatMoney(reconciliation.investingNet) }}
          </Cell>
          <Cell>
            <strong>筹资活动净额：</strong> {{ formatMoney(reconciliation.financingNet) }}
          </Cell>
        </Row>
        <Row type="flex" :space-x="20" class="margin-top">
          <Cell>
            <strong>分类现金流净额合计：</strong> {{ formatMoney(reconciliation.classifiedNetTotal) }}
          </Cell>
          <Cell>
            <strong>期初现金余额：</strong> {{ formatMoney(reconciliation.beginningCashBalance) }}
          </Cell>
          <Cell>
            <strong>期末现金余额：</strong> {{ formatMoney(reconciliation.endingCashBalance) }}
          </Cell>
          <Cell>
            <strong>实际现金变动：</strong> {{ formatMoney(reconciliation.actualCashChange) }}
          </Cell>
        </Row>
      </div>
    </div>
  </app-content>
</template>

<script>
import moment from 'moment';

export default {
  name: "CashFlowStatement",
  data() {
    return {
      report: null,
      reportData: {},
      accountDate: moment().format('YYYY-MM-DD'),
      loading: false,
      reconciliation: null
    }
  },
  computed: {
    templateItems() {
      return this.report ? this.report.items : [];
    }
  },
  methods: {
    async doSearch() {
      if (!this.accountDate) {
        this.$Message.warning('请选择会计期间');
        return;
      }
      this.loading = true;
      try {
        const m = moment(this.accountDate);
        await this.$api.cashflow.recalculate(m.year(), m.month() + 1);
      } catch (e) {
        this.$Message.error('现金流量重算失败，请检查现金科目和分类规则后重试');
        this.loading = false;
        return;
      }
      await this.loadReport();
      await this.loadReconciliation();
    },
    async loadReport() {
      this.loading = true;
      try {
        // 查询现金流量表模板 (type=2)
        const templateRes = await this.$api.report.template.list();
        if (templateRes.success && templateRes.data) {
          const cashFlowTemplate = templateRes.data.find(t => t.type === 2);
          if (cashFlowTemplate) {
            const detailRes = await this.$api.report.template.load(cashFlowTemplate.id);
            if (detailRes.success) {
              this.report = detailRes.data;
              await this.loadReportData();
            }
          } else {
            this.$Message.warning('未找到现金流量表模板');
          }
        }
      } catch (e) {
        console.error('加载报表失败', e);
        this.$Message.error('加载报表失败');
      } finally {
        this.loading = false;
      }
    },
    async loadReportData() {
      if (!this.report || !this.accountDate) return;

      try {
        const res = await this.$api.report.view(this.report.id, { accountDate: this.accountDate });
        if (res.success) {
          this.reportData = res.data || {};
        }
      } catch (e) {
        console.error('加载报表数据失败', e);
      }
    },
    async loadReconciliation() {
      if (!this.accountDate) return;

      try {
        const res = await this.$api.cashflow.reconcile(this.accountDate);
        if (res.success) {
          this.reconciliation = res.data;
        }
      } catch (e) {
        console.error('加载勾稽状态失败', e);
      }
    },
    formatMoney(amount) {
      if (amount === null || amount === undefined) return '-';
      return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    goToPending() {
      this.$router.push({ name: 'CashFlowPending' });
    },
    doExport() {
      if (!this.report) {
        this.$Message.warning('请先查询报表');
        return;
      }
      const params = `templateId=${this.report.id}&accountDate=${encodeURIComponent(this.accountDate)}`;
      this.downloadFile('/api/report/template/export?' + params);
    },
    downloadFile(url) {
      let xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.onload = () => {
        if (xhr.status === 200) {
          let blob = xhr.response;
          if (blob.type && blob.type.indexOf('json') !== -1) {
            let reader = new FileReader();
            reader.onload = () => {
              try {
                let result = JSON.parse(reader.result);
                this.$Message(result.msg || '导出失败');
              } catch (e) {
                this.$Message('导出失败');
              }
            };
            reader.readAsText(blob);
            return;
          }
          let disposition = xhr.getResponseHeader('Content-Disposition');
          let fileName = '现金流量表.xlsx';
          if (disposition) {
            let match = disposition.match(/filename\*=UTF-8''(.+)/) || disposition.match(/filename="?(.+?)"?$/);
            if (match) fileName = decodeURIComponent(match[1]);
          }
          let downloadUrl = window.URL.createObjectURL(blob);
          let a = document.createElement('a');
          a.href = downloadUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          this.$Message('导出失败');
        }
      };
      xhr.onerror = () => { this.$Message('导出失败'); };
      xhr.send();
    }
  },
  mounted() {
    this.doSearch();
  }
}
</script>

<style scoped>
div.font-bold {
  font-size: 14px;
}

.reconciliation-status {
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  border: 1px solid;
}

.reconciliation-status.passed {
  background-color: #f6ffed;
  border-color: #b7eb8f;
}

.reconciliation-status.failed {
  background-color: #fff2f0;
  border-color: #ffccc7;
}

.reconciliation-detail {
  margin-top: 24px;
  padding: 16px;
  background-color: #fafafa;
  border-radius: 4px;
}

.reconciliation-detail h4 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: bold;
}

.text-success {
  color: #52c41a;
}

.text-danger {
  color: #f5222d;
}

.text-warning {
  color: #faad14;
}

.margin-top {
  margin-top: 12px;
}
</style>
